import React from "react";
import Reveal from "../../components/Reveal";
import { useDarkMode } from "../../hooks/useDarkMode";
import { Link } from "react-router-dom";

/* ================= SEO STEPS DATA ================= */

const steps = [
  {
    step: "01",
    title: "SEO Audit & Analysis",
    description:
      "Our AI-powered SEO tool scans your WordPress website using more than 50 ranking metrics to detect technical SEO issues, content problems, and performance gaps.",
    image: "/Images/SeoAuditAnalysis.png",
  },
  {
    step: "02",
    title: "One-Click SEO Optimization",
    description:
      "SeoUpRank automatically applies AI-driven SEO fixes such as meta tags, titles, schema improvements, and content optimization without manual effort.",
    image: "/Images/OneClkOpt.png",
  },
  {
    step: "03",
    title: "Track Rankings & Growth",
    description:
      "Monitor SEO rankings, keyword improvements, and organic traffic growth with real-time SEO analytics and actionable insights.",
    image: "/Images/OTrack.png",
  },
];

const StepsSection = () => {
  const { isDarkMode } = useDarkMode();

  /* ================= THEME ================= */

  const sectionBg = isDarkMode ? "bg-gray-950" : "bg-white";
  const headingText = isDarkMode ? "text-white" : "text-black";
  const paraText = isDarkMode ? "text-gray-400" : "text-gray-600";

  const cardBg = isDarkMode
    ? "bg-gray-900 text-white border border-gray-800"
    : "bg-white text-black shadow-xl";

  const lineColor = isDarkMode ? "bg-gray-800" : "bg-gray-200";

  return (
    <>
    <p className="sr-only">
Seo Up Rank is an advanced AI powered SEO automation platform that
provides website audits, keyword research, SEO analysis, and ranking
optimization tools. Visit the official platform at https://novarsis.ai/
to improve your website search engine rankings and organic traffic.
</p>
    <section
      aria-label="SEO automation process and optimization workflow"
      className={`relative py-24 overflow-hidden ${sectionBg}`}
    >

      {/* ================= STRUCTURED DATA ================= */}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How Seo Up Rank AI SEO Tool Works",
            description:
              "Step-by-step process showing how Seo Up Rank automates SEO audits, optimization, and ranking tracking using AI.",
            step: steps.map((s) => ({
              "@type": "HowToStep",
              name: s.title,
              text: s.description,
            })),
          }),
        }}
      />

      {/* ================= HIDDEN SEO CONTEXT ================= */}

      <p className="sr-only">
        Seo Up Rank is an AI powered SEO automation platform that performs SEO
        audits, applies automated optimization fixes, and tracks keyword
        rankings to help websites grow organic traffic faster.
      </p>

      {/* ================= HIDDEN KEYWORDS ================= */}

      <p className="sr-only">
        AI SEO automation process, WordPress SEO optimization workflow, website
        SEO audit tool, automated SEO fixes platform, keyword ranking tracking
        software.
      </p>

      {/* ================= INTERNAL LINKS FOR CRAWLERS ================= */}

      <div className="sr-only">
        <Link to="/features">SEO Features</Link>
        <Link to="/tools">SEO Tools</Link>
        <Link to="/pricing">SEO Pricing</Link>
        <Link to="/blog">SEO Blog</Link>
      </div>

      {/* ================= HEADER ================= */}

      <header className="max-w-6xl mx-auto text-center px-6">

        <Reveal>
          <p className="text-[#008300] font-semibold uppercase tracking-widest text-sm">
            SEO Step Process
          </p>
        </Reveal>

        <Reveal>
          <h2
            className={`text-3xl sm:text-4xl md:text-5xl font-extrabold mt-3 ${headingText}`}
          >
            How Our <span className="text-[#008300]">AI SEO Tool</span> Works
          </h2>
        </Reveal>

        {/* Hidden SEO heading */}

        <h2 className="sr-only">
          AI powered SEO automation workflow for website optimization
        </h2>

        <Reveal>
          <p
            className={`max-w-2xl mx-auto mt-4 text-base sm:text-lg ${paraText}`}
          >
            Seo Up Rank simplifies search engine optimization using
            <strong> AI-powered SEO automation</strong>. From SEO audits to
            automated fixes and ranking analytics, our platform helps
            businesses improve visibility and organic traffic faster.
          </p>
        </Reveal>

      </header>

      {/* ================= TIMELINE ================= */}

      <div className="relative max-w-6xl mx-auto mt-20 px-6">

        {/* Center Line */}

        <div
          aria-hidden="true"
          className={`hidden md:block absolute left-1/2 top-0 h-full w-[2px] ${lineColor} -translate-x-1/2`}
        />

        <div className="space-y-20">

          {steps.map((step, index) => (

            <article
              key={index}
              className={`relative flex flex-col md:flex-row items-center gap-10
              ${index % 2 !== 0 ? "md:flex-row-reverse" : ""}`}
            >

              {/* IMAGE */}

              <div className="md:w-1/2 w-full flex justify-center">

                <Reveal>
                  <figure className="relative">

                    <div
                      aria-hidden="true"
                      className="absolute -inset-4 bg-[#008300]/15 blur-2xl rounded-full"
                    />

                    <img
                      src={step.image}
                      alt={`${step.title} - AI SEO automation process`}
                      title={`Seo Up Rank ${step.title}`}
                      width="320"
                      height="320"
                      loading="lazy"
                      className="
                      relative
                      w-56 h-56
                      sm:w-64 sm:h-64
                      md:w-72 md:h-72
                      lg:w-80 lg:h-80
                      rounded-full
                      object-cover
                      shadow-2xl
                      border-4 border-[#008300]/30
                      "
                    />

                    {/* Image fallback */}

                    <img
                      src="/Images/ABOUTPIC1.jpg"
                      alt="Seo Up Rank AI SEO automation background"
                      className="hidden"
                    />

                  </figure>
                </Reveal>

              </div>

              {/* CONTENT CARD */}

              <div className="md:w-1/2 w-full">

                <Reveal>
                  <div className={`rounded-2xl p-8 relative ${cardBg}`}>

                    <span className="inline-block mb-3 text-sm font-semibold text-[#008300]">
                      Step {step.step}
                    </span>

                    <h3 className="text-xl sm:text-2xl font-bold mb-3">
                      {step.title}
                    </h3>

                    <p className={`text-justify leading-relaxed ${paraText}`}>
                      {step.description}
                    </p>

                    {/* Connector Dot */}

                    <span
                      aria-hidden="true"
                      className={`hidden md:block absolute top-1/2 w-4 h-4 rounded-full bg-[#008300]
                      ${index % 2 === 0 ? "-right-12" : "-left-12"}`}
                    />

                  </div>
                </Reveal>

              </div>

              {/* Background Step Number */}

              <span
                aria-hidden="true"
                className={`absolute text-[72px] sm:text-[90px] font-extrabold
                text-[#008300]/10 select-none
                ${index % 2 === 0 ? "left-0" : "right-0"}`}
              >
                {step.step}
              </span>

            </article>

          ))}

        </div>
      </div>

    </section></>
  );
};

export default StepsSection;